import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Paper,
  TableContainer,
  Tabs,
  Tab,
  useMediaQuery,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import api from "../../utils/api";
import ViolationBillingSetupManager from "./ViolationBillingSetupManager";
import ViolationAdvancedSettings from "./ViolationAdvancedSettings";
import ViolationFormBuilder from "./ViolationFormBuilder";
import ModuleStatuses from "./ModuleStatuses";
import ViolationManual from "./ViolationManual";

const ExpandableCellText = ({
  text,
  isMobile,
  shortLimit = 80,
  longLimit = 220,
}) => {
  const [expanded, setExpanded] = useState(false);

  if (!text) return <Typography variant="body2">-</Typography>;

  const limit = isMobile ? shortLimit : longLimit;
  const shouldTruncate = String(text).length > limit;
  const displayText =
    !shouldTruncate || expanded ? String(text) : `${String(text).slice(0, limit)}...`;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography
        variant="body2"
        sx={{
          whiteSpace: "normal",
          wordBreak: "break-word",
          overflowWrap: "anywhere",
          lineHeight: 1.5,
          color: "#223142",
        }}
      >
        {displayText}
      </Typography>

      {shouldTruncate && (
        <Button
          size="small"
          onClick={() => setExpanded((prev) => !prev)}
          sx={{
            mt: 0.5,
            px: 0,
            minWidth: "auto",
            textTransform: "none",
            fontWeight: 600,
            color: "#2f63ad",
          }}
        >
          {expanded ? "Show less" : "Show more"}
        </Button>
      )}
    </Box>
  );
};

const ViolationManager = () => {
  const isMobile = useMediaQuery("(max-width:768px)");
  const [tabIndex, setTabIndex] = useState(0);
  const [types, setTypes] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    title: "",
    ccr_article_id: "",
    resolution_step: "",
    days_to_cure: "",
    rule: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    try {
      const res = await api.get("/getCcrViolationTypes");
      setTypes(res.data || []);
    } catch (err) {
      console.error("Failed to fetch types", err);
    }
  };

  const openAddDialog = () => {
    setFormData({
      id: null,
      title: "",
      ccr_article_id: "",
      resolution_step: "",
      days_to_cure: "",
      rule: "",
    });
    setIsEditing(false);
    setOpenDialog(true);
  };

  const openEditDialog = (type) => {
    setFormData({
      id: type.id,
      title: type.title || "",
      ccr_article_id: type.ccr_article_id || "",
      resolution_step: type.resolution_step || "",
      days_to_cure: type.days_to_cure || "",
      rule: type.rule || "",
    });
    setIsEditing(true);
    setOpenDialog(true);
  };

  const closeDialog = () => {
    setOpenDialog(false);
    setFormData({
      id: null,
      title: "",
      ccr_article_id: "",
      resolution_step: "",
      days_to_cure: "",
      rule: "",
    });
    setIsEditing(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async () => {
    try {
      const payload = {
        title: formData.title,
        ccr_article_id: formData.ccr_article_id || null,
        resolution_step: formData.resolution_step,
        days_to_cure: formData.days_to_cure || null,
        rule: formData.rule || null,
      };

      if (isEditing) {
        await api.put(`/updateCcrViolationType/${formData.id}`, payload);
        setSnackbar({
          open: true,
          message: "Violation type updated successfully",
          severity: "success",
        });
      } else {
        await api.post(`/createCcrViolationType`, payload);
        setSnackbar({
          open: true,
          message: "Violation type added successfully",
          severity: "success",
        });
      }

      fetchTypes();
      closeDialog();
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error saving violation type",
        severity: "error",
      });
    }
  };

  const deleteType = async (id) => {
    if (!window.confirm("Are you sure you want to delete this type?")) return;

    try {
      await api.delete(`/deleteCcrViolationType/${id}`);
      setSnackbar({
        open: true,
        message: "Violation type deleted",
        severity: "success",
      });
      fetchTypes();
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error deleting violation type",
        severity: "error",
      });
    }
  };

  const dialogPaperSx = {
    borderRadius: "18px",
    background: "rgba(255,255,255,0.96)",
    backdropFilter: "blur(10px)",
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
    overflow: "hidden",
  };

  const fieldSx = {
    mt: 1.25,
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 600,
  };

  const textCellSx = {
    verticalAlign: "top",
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
          Violation Manager
        </Typography>
        <Typography variant="body2" sx={{ color: "#6b778c" }}>
          Manage CCR violation types, billing rules, and cure timelines.
        </Typography>
      </Box>

      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        sx={{
          mb: 3,
          minHeight: "auto",
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
            minHeight: 42,
            color: "#5f6b7a",
            mr: 1,
            px: 2,
          },
          "& .Mui-selected": {
            color: "#2f63ad !important",
            backgroundColor: "rgba(47,99,173,0.08)",
          },
          "& .MuiTabs-indicator": { display: "none" },
        }}
      >
        <Tab label="Violation Types" />
        <Tab label="Billing Setup" />
          <Tab label="Statuses" />
          <Tab label="Form Builder" />
          <Tab label="Advanced" />
          <Tab label="Manual" />
      </Tabs>

      {tabIndex === 1 && <ViolationBillingSetupManager />}
      {tabIndex === 2 && <ModuleStatuses moduleId={2} moduleName="Violations" />}
      {tabIndex === 3 && <ViolationFormBuilder />}
      {tabIndex === 4 && <ViolationAdvancedSettings />}
      {tabIndex === 5 && <ViolationManual />}

      {tabIndex === 0 && (
      <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: "#223142",
              mb: 0.5,
            }}
          >
            Violation Types
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "#6b778c",
            }}
          >
            Manage CCR violation types, cure timelines, resolution steps, and rules.
          </Typography>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAddDialog}
          sx={primaryButtonSx}
        >
          Add Violation Type
        </Button>
      </Box>

      <Paper
        elevation={0}
        sx={{
          borderRadius: "16px",
          border: "1px solid rgba(47,99,173,0.08)",
          boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
          overflow: "hidden",
          backgroundColor: "rgba(255,255,255,0.96)",
        }}
      >
        <TableContainer>
          <Table
            sx={{
              tableLayout: "fixed",
              minWidth: 1000,
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, width: "18%" }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 700, width: "14%" }}>Article ID</TableCell>
                <TableCell sx={{ fontWeight: 700, width: "20%" }}>
                  Resolution Step
                </TableCell>
                <TableCell sx={{ fontWeight: 700, width: "10%" }}>
                  Days to Cure
                </TableCell>
                <TableCell sx={{ fontWeight: 700, width: "28%" }}>Rule</TableCell>
                <TableCell sx={{ fontWeight: 700, width: "10%" }} align="right">
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {types.length > 0 ? (
                types.map((type) => (
                  <TableRow key={type.id}>
                    <TableCell sx={textCellSx}>
                      <ExpandableCellText
                        text={type.title}
                        isMobile={isMobile}
                        shortLimit={50}
                        longLimit={90}
                      />
                    </TableCell>

                    <TableCell sx={textCellSx}>
                      <ExpandableCellText
                        text={type.ccr_article_id}
                        isMobile={isMobile}
                        shortLimit={40}
                        longLimit={70}
                      />
                    </TableCell>

                    <TableCell sx={textCellSx}>
                      <ExpandableCellText
                        text={type.resolution_step}
                        isMobile={isMobile}
                        shortLimit={70}
                        longLimit={140}
                      />
                    </TableCell>

                    <TableCell sx={{ verticalAlign: "top" }}>
                      <Typography variant="body2">
                        {type.days_to_cure || "N/A"}
                      </Typography>
                    </TableCell>

                    <TableCell sx={textCellSx}>
                      <ExpandableCellText
                        text={type.rule}
                        isMobile={isMobile}
                        shortLimit={120}
                        longLimit={220}
                      />
                    </TableCell>

                    <TableCell align="right" sx={{ verticalAlign: "top" }}>
                      <IconButton onClick={() => openEditDialog(type)}>
                        <Edit />
                      </IconButton>
                      <IconButton color="error" onClick={() => deleteType(type.id)}>
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} sx={{ color: "text.secondary" }}>
                    No violation types found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      </Box>
      )}

      <Dialog
        open={openDialog}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 700,
            fontSize: "1.35rem",
            pt: 3,
            pb: 1,
            color: "#1f1f1f",
          }}
        >
          {isEditing ? "Edit Violation Type" : "Add Violation Type"}
        </DialogTitle>

        <DialogContent sx={{ px: 3, pb: 1 }}>
          <Typography
            sx={{
              mt: 0.5,
              mb: 1,
              textAlign: "center",
              color: "#666",
              fontSize: "0.95rem",
            }}
          >
            Enter the details for this violation type.
          </Typography>

          <TextField
            label="Title"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            fullWidth
            sx={fieldSx}
          />

          <TextField
            label="CCR Article ID"
            name="ccr_article_id"
            value={formData.ccr_article_id}
            onChange={handleInputChange}
            fullWidth
            sx={fieldSx}
          />

          <TextField
            label="Resolution Step"
            name="resolution_step"
            value={formData.resolution_step}
            onChange={handleInputChange}
            fullWidth
            sx={fieldSx}
          />

          <TextField
            label="Days to Cure"
            name="days_to_cure"
            value={formData.days_to_cure}
            onChange={handleInputChange}
            type="number"
            fullWidth
            sx={fieldSx}
          />

          <TextField
            label="Rule"
            name="rule"
            value={formData.rule}
            onChange={handleInputChange}
            multiline
            minRows={6}
            fullWidth
            sx={fieldSx}
          />
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1.5,
            justifyContent: "center",
            gap: 1.25,
          }}
        >
          <Button onClick={closeDialog} variant="outlined" sx={secondaryButtonSx}>
            Cancel
          </Button>

          <Button variant="contained" onClick={handleFormSubmit} sx={primaryButtonSx}>
            {isEditing ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ViolationManager;
